
import React from "react";
import { Helmet } from "react-helmet";
import SEOMetadata from '@/components/SEOMetadata';
import HeroSection from "@/components/HeroSection";
import CTASection from "@/components/CTASection";

// Import the component sections
import CoachingRegionalInfo from "@/components/coaching/CoachingRegionalInfo";
import ServicesSection from "@/components/coaching/ServicesSection";
import CoachingApproach from "@/components/coaching/CoachingApproach";
import BridgeSection from "@/components/coaching/BridgeSection";
import FamilyMediationSection from "@/components/coaching/FamilyMediationSection";
import FAQSection from "@/components/coaching/FAQSection";
import TestimonialsSection from "@/components/coaching/TestimonialsSection";
import FormOfAddressNote from "@/components/coaching/FormOfAddressNote";
import SpecialFormatsInspiration from "@/components/coaching/SpecialFormatsInspiration";
import { webPageStructuredData } from '@/data/structuredData';

const Coaching = () => {
  return (
    <>
      <SEOMetadata
        metaTitle="Coaching für Privatpersonen | Jörg Hörmann Bayern"
        metaDescription="Individuelles Coaching für persönliche Entwicklung in Nürnberg, München + Remote. Authentische Begleitung auf Augenhöhe. ✆ 09171-9892118"
        title="Coaching für Privatpersonen | Jörg Hörmann Bayern"
        description="Individuelles Coaching für persönliche Entwicklung. Termine in Nürnberg, Ingolstadt, München + Remote. Authentische Begleitung auf Augenhöhe."
        imageUrl="/images/og-portrait.jpg"
        pageName="Coaching"
        primaryLink="/coaching"
        keywords="Coaching Bayern, Lebenscoaching Nürnberg, Persönlichkeitsentwicklung München, Coaching Ingolstadt, Individuelles Coaching Remote"
        canonicalUrl="https://joerg-hoermann.de/coaching"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(webPageStructuredData({
            name: "Ganzheitliches Coaching für Privatpersonen",
            description: "Individuelles Coaching für persönliche Entwicklung und Lebensgestaltung. Nürnberg, München + Remote.",
            url: "https://joerg-hoermann.de/coaching",
            aboutTopics: ["finanzcoaching", "verhaltungsoekonomie", "selbstfuehrung", "resilienz"]
          }))}
        </script>
      </Helmet>
      <main>
      {/* 1. Hero Section - Stays first (emotional connection) */}
      <HeroSection
        title="Der Mensch hinter den Zahlen: Dein Weg zu finanzieller Selbstbestimmung"
        subtitle="Finanzcoaching für Privatpersonen"
        description="Ich begleite dich auf deinem persönlichen Weg zu einem selbstbestimmten Umgang mit Geld und Finanzen – unabhängig, verständlich und immer mit deinen individuellen Bedürfnissen und Werten im Mittelpunkt."
        backgroundClass="bg-joerg-50"
        imageUrl="/lovable-uploads/1d230a42-fa65-4100-9bc6-7317dec21070.png"
        hideHeroLogo={true}
      />

      {/* Golden Snippet – AEO Definition */}
      <section className="py-10 bg-white" data-speakable="definition">
        <div className="container mx-auto px-4">
          <p className="max-w-3xl mx-auto text-lg lg:text-xl text-joerg-700 leading-relaxed italic border-l-4 border-primary/30 pl-6">
            <strong className="not-italic">Was ist Finanzcoaching?</strong>{" "}
            Finanzcoaching ist eine psychologisch fundierte Begleitung, die über die klassische Anlageberatung hinausgeht. Es kombiniert strategische Finanzplanung mit <strong>Behavioral Finance</strong>, um tiefliegende Geldblockaden zu lösen und eine dauerhafte finanzielle Selbstbestimmung im Einklang mit persönlichen Werten zu ermöglichen – für Privatpersonen, Entscheider und Menschen in komplexen Lebenssituationen.
          </p>
        </div>
      </section>

      {/* AI-Summary / AEO Abstract */}
      <section className="sr-only" aria-label="Zusammenfassung" data-speakable="summary">
        <p>Jörg Hörmann bietet individuelles Coaching für Privatpersonen in Bayern und deutschlandweit an. Der Schwerpunkt liegt auf finanzieller Selbstbestimmung, persönlicher Entwicklung und einem gesunden Umgang mit Geld – wissenschaftlich fundiert durch Verhaltensökonomie und Resilienzforschung.</p>
      </section>

      {/* 2. Form of Address Note - Trust building */}
      <FormOfAddressNote />

      {/* 3. Testimonials Section - Social proof early */}
      <TestimonialsSection />

      {/* 4. Regional Info - Local SEO optimization */}
      <CoachingRegionalInfo />

      {/* 5. Services Section - Value offering */}
      <ServicesSection />
      
      {/* 6. FAQ Section - Moved up to address questions when they naturally arise */}
      <FAQSection />

      {/* 7. Coaching Approach - Methodology after basic questions are answered */}
      <CoachingApproach />

      {/* 8. Bridge Section - Transitions to specific offerings */}
      <BridgeSection />

      {/* 9. Special Formats & Inspiration - Combined section for reduced cognitive load */}
      <SpecialFormatsInspiration />

      {/* 10. Family Mediation - Specific service highlight */}
      <FamilyMediationSection />


      {/* 13. CTA Section - Updated with new gradient background */}
      <CTASection
        title="Bereit, dich als Mensch in den Mittelpunkt zu stellen?"
        description="Vereinbare ein kostenfreies Erstgespräch und erfahre, wie mein ganzheitlicher Ansatz dir auf deinem Weg zu finanzieller Selbstbestimmung helfen kann."
        primaryCTA={{ text: "Gespräch vereinbaren", link: "/kontakt" }}
        backgroundClass="bg-gradient-to-br from-[#EDF5E9] to-[#E8F1FB] text-joerg-800"
      />
    </main>
    </>
  );
};

export default Coaching;
